import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const NAVY = [30, 58, 138];
const INK = [15, 23, 42];
const MUTED = [100, 116, 139];
const LINE = [226, 232, 240];
const PAGE_BG = [240, 242, 245];
const WHITE = [255, 255, 255];
const ROW_ALT = [248, 250, 252];
const GREEN = [21, 128, 61];
const AMBER = [180, 83, 9];
const RED = [185, 28, 28];

const BASE = process.env.PUBLIC_URL || "";
const LOGO_URL = `${BASE}/oticLogo.png`;

// jsPDF mangles any character outside Latin-1 (the naira sign, curly
// quotes, long dashes). Clean every string before it's drawn.
const clean = (s) =>
  String(s ?? "")
    .replace(/\u20A6/g, "NGN ")
    .replace(/[\u2012-\u2015]/g, "-")
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[^\x00-\xFF]/g, "");

const ngn = (n) =>
  "NGN " + Number(n || 0).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "-";

function durationText(m) {
  if (m.duration_days == null) return "-";
  const d = m.duration_days;
  const t = d < 60 ? `${d} day${d === 1 ? "" : "s"}` : `${Math.round(d / 30)} months`;
  return m.is_completed ? t : `${t} so far`;
}

function expiryText(days) {
  if (days == null) return "-";
  if (days < 0) return `Expired ${Math.abs(days)}d ago`;
  if (days === 0) return "Expires today";
  return `${days}d left`;
}

/* ---------- asset loading ---------- */

function loadLogo(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        c.getContext("2d").drawImage(img, 0, 0);
        resolve({ dataUrl: c.toDataURL("image/png"), w: img.naturalWidth, h: img.naturalHeight });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

async function fetchFontBase64(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error("font fetch failed");
  const bytes = new Uint8Array(await res.arrayBuffer());
  // A dev server returns index.html (HTTP 200) for a missing file.
  // A real TrueType file starts with 00 01 00 00.
  if (bytes[0] !== 0x00 || bytes[1] !== 0x01) throw new Error("not a TTF file");
  let bin = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  }
  return btoa(bin);
}

async function registerTahoma(doc) {
  try {
    const [regular, bold] = await Promise.all([
      fetchFontBase64(`${BASE}/fonts/tahoma.ttf`),
      fetchFontBase64(`${BASE}/fonts/tahomabd.ttf`),
    ]);
    doc.addFileToVFS("tahoma.ttf", regular);
    doc.addFont("tahoma.ttf", "Tahoma", "normal");
    doc.addFileToVFS("tahomabd.ttf", bold);
    doc.addFont("tahomabd.ttf", "Tahoma", "bold");
    return "Tahoma";
  } catch {
    return "helvetica";
  }
}

/* ---------- main ---------- */

export async function exportPlanPdf(plan, summaryText) {
  const m = plan.metrics || {};
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const FONT = await registerTahoma(doc);
  const logo = await loadLogo(LOGO_URL);

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 36;
  const contentW = pageW - M * 2;
  let y = M;

  const contact = plan.contact || {};
  const phoneLine = (contact.phones || []).join(", ");

  const font = (style = "normal", size = 10, color = INK) => {
    doc.setFont(FONT, style);
    doc.setFontSize(size);
    doc.setTextColor(...color);
  };

  // Paint the grey page background once per page (idempotent, because
  // autoTable's page hook also fires on the first page).
  const paintedPages = new Set();
  const paintBg = () => {
    const n = doc.getCurrentPageInfo().pageNumber;
    if (paintedPages.has(n)) return;
    paintedPages.add(n);
    doc.setFillColor(...PAGE_BG);
    doc.rect(0, 0, pageW, pageH, "F");
  };

  const newPage = () => {
    doc.addPage();
    paintBg();
    y = M;
  };
  const ensureSpace = (needed) => {
    if (y + needed > pageH - 60) newPage();
  };

  const panel = (x, py, w, h) => {
    doc.setFillColor(...WHITE);
    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.8);
    doc.roundedRect(x, py, w, h, 8, 8, "FD");
  };

  const sectionTitle = (text) => {
    ensureSpace(80);
    font("bold", 9, NAVY);
    doc.text(clean(text).toUpperCase(), M + 2, y + 8);
    y += 18;
  };

  paintBg();

  /* ----- header panel ----- */
  const headerH = 64;
  panel(M, y, contentW, headerH);
  let textX = M + 18;
  if (logo) {
    const h = 38;
    const w = Math.min((logo.w / logo.h) * h, 120);
    doc.addImage(logo.dataUrl, "PNG", M + 16, y + (headerH - h) / 2, w, h);
    textX = M + 16 + w + 14;
  }
  font("bold", 15, INK);
  doc.text("OTIC Geosystems", textX, y + 28);
  font("normal", 9, MUTED);
  doc.text("GNSS Equipment & Services", textX, y + 43);

  font("bold", 12, NAVY);
  doc.text("Payment Plan Summary", pageW - M - 18, y + 28, { align: "right" });
  font("normal", 9, MUTED);
  doc.text(`Generated ${fmt(new Date().toISOString())}`, pageW - M - 18, y + 43, { align: "right" });
  y += headerH + 12;

  /* ----- customer panel ----- */
  const custH = 88;
  panel(M, y, contentW, custH);
  font("bold", 17, INK);
  doc.text(clean(plan.customer_name || "-"), M + 18, y + 27);

  const statusText = m.is_completed ? "COMPLETED" : "IN PROGRESS";
  font("bold", 8, WHITE);
  const pillW = doc.getTextWidth(statusText) + 22;
  doc.setFillColor(...(m.is_completed ? GREEN : AMBER));
  doc.roundedRect(pageW - M - 18 - pillW, y + 14, pillW, 18, 9, 9, "F");
  doc.text(statusText, pageW - M - 18 - pillW / 2, y + 26, { align: "center" });

  font("normal", 10, MUTED);
  doc.text(
    clean(`${plan.display_name || plan.equipment_name || "-"}   |   Purchased ${fmt(m.date_purchased)}   |   ${durationText(m)}`),
    M + 18, y + 47
  );

  // divider + contact details
  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.5);
  doc.line(M + 18, y + 57, M + contentW - 18, y + 57);

  font("bold", 8, MUTED);
  doc.text("EMAIL", M + 18, y + 71);
  doc.text("PHONE", M + contentW / 2, y + 71);
  font("normal", 9.5, INK);
  const emailText = doc.splitTextToSize(clean(contact.email || "-"), contentW / 2 - 40)[0];
  const phoneText = doc.splitTextToSize(clean(phoneLine || "-"), contentW / 2 - 24)[0];
  doc.text(emailText, M + 18, y + 82);
  doc.text(phoneText, M + contentW / 2, y + 82);
  y += custH + 12;

  /* ----- stat cards ----- */
  const gap = 10;
  const cardW = (contentW - gap * 3) / 4;
  const cardH = 56;
  const cards = [
    { label: "TOTAL", value: ngn(m.total_price), color: INK },
    { label: "PAID", value: ngn(m.total_paid), color: GREEN },
    { label: "BALANCE", value: ngn(m.balance), color: m.balance > 0 ? AMBER : GREEN },
    { label: "LAST PAYMENT", value: fmt(m.last_payment_at), color: INK },
  ];
  cards.forEach((c, i) => {
    const x = M + i * (cardW + gap);
    panel(x, y, cardW, cardH);
    font("normal", 7.5, MUTED);
    doc.text(c.label, x + 12, y + 20);
    font("bold", 10.5, c.color);
    doc.text(c.value, x + 12, y + 40);
  });
  y += cardH + 12;

  /* ----- progress panel ----- */
  const progH = 56;
  panel(M, y, contentW, progH);
  const pct = Math.max(0, Math.min(100, m.percent ?? 0));
  const barX = M + 18;
  const barW = contentW - 36;
  doc.setFillColor(...LINE);
  doc.roundedRect(barX, y + 16, barW, 10, 5, 5, "F");
  if (pct > 0) {
    doc.setFillColor(...(pct >= 100 ? GREEN : pct >= 50 ? NAVY : AMBER));
    doc.roundedRect(barX, y + 16, Math.max((barW * pct) / 100, 10), 10, 5, 5, "F");
  }
  font("bold", 9.5, INK);
  doc.text(`${pct}% paid`, barX, y + 44);
  font("normal", 9.5, MUTED);
  doc.text(`${ngn(m.balance)} remaining`, barX + barW, y + 44, { align: "right" });
  y += progH + 16;

  /* ----- summary panel ----- */
  if (summaryText) {
    font("normal", 10, INK);
    const lines = doc.splitTextToSize(clean(summaryText), contentW - 36);
    const sumH = 38 + lines.length * 14;
    ensureSpace(sumH + 12);
    panel(M, y, contentW, sumH);
    font("bold", 9, NAVY);
    doc.text("SUMMARY", M + 18, y + 22);
    font("normal", 10, INK);
    doc.text(lines, M + 18, y + 40, { lineHeightFactor: 1.35 });
    y += sumH + 16;
  }

  /* ----- tables ----- */
  const tableBase = {
    margin: { left: M, right: M, bottom: 60 },
    theme: "grid",
    styles: { font: FONT, fontSize: 9, cellPadding: 7, textColor: INK, lineColor: LINE, lineWidth: 0.5, fillColor: WHITE },
    headStyles: { fillColor: NAVY, textColor: 255, fontStyle: "bold", lineColor: NAVY },
    alternateRowStyles: { fillColor: ROW_ALT },
    willDrawPage: paintBg,
  };

  const history = plan.payment_history || [];
  sectionTitle(`Payment History (${history.length})`);
  autoTable(doc, {
    ...tableBase,
    startY: y,
    head: [["Date", "Amount", "Status", "Note"]],
    body: history.length
      ? history.map((p) => [fmt(p.date), ngn(p.amount), clean(p.status), clean(p.note)])
      : [["No payments recorded", "", "", ""]],
    columnStyles: { 1: { fontStyle: "bold" }, 2: { cellWidth: 75 } },
    didParseCell: (d) => {
      if (d.section === "body" && d.column.index === 2) {
        const s = String(d.cell.raw).toLowerCase();
        d.cell.styles.fontStyle = "bold";
        d.cell.styles.textColor = s === "confirmed" ? GREEN : s === "rejected" ? RED : AMBER;
      }
    },
  });
  y = doc.lastAutoTable.finalY + 22;

  const serials = plan.serials || [];
  if (serials.length) {
    sectionTitle("Device Serials & Activation Codes");
    autoTable(doc, {
      ...tableBase,
      startY: y,
      head: [["Serial", "Activation code", "Code expiry", "Status"]],
      body: serials.map((s) => [
        clean(s.serial_number), clean(s.current_code || "-"), fmt(s.code_expiry), expiryText(s.days_until_expiry),
      ]),
      didParseCell: (d) => {
        if (d.section !== "body") return;
        if (d.column.index <= 1) {
          d.cell.styles.font = "courier";
          d.cell.styles.fontStyle = "bold";
        }
        if (d.column.index === 3) {
          const days = serials[d.row.index]?.days_until_expiry;
          d.cell.styles.fontStyle = "bold";
          d.cell.styles.textColor = days == null ? MUTED : days < 0 ? RED : days <= 7 ? AMBER : GREEN;
        }
      },
    });
  }

  /* ----- footer on every page ----- */
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    font("normal", 8, MUTED);
    doc.text("OTIC Geosystems  |  Confidential - contains customer details and activation codes", M, pageH - 24);
    doc.text(`Page ${i} of ${pages}`, pageW - M, pageH - 24, { align: "right" });
  }

  const safe = (s) => String(s || "plan").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();
  doc.save(`plan-${safe(plan.customer_name)}-${safe(plan.display_name || plan.equipment_name)}.pdf`);
}